import React, { useEffect, useState } from 'react';
import { getSales } from '../constants/api';
import { useAuth } from '../constants/AuthContext';
import { COLORS } from '../constants/theme';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function ReportsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'Owner';
  const isMobile = window.innerWidth < 768;
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('This Week');

  useEffect(() => {
    getSales()
      .then(res => setSales(res.data))
      .catch(err => console.log(err.message))
      .finally(() => setLoading(false));
  }, []);

  const getFiltered = () => {
    const now = new Date();
    let result = sales.filter(s => !s.isVoided);
    if (filter === 'Today') return result.filter(s => new Date(s.date).toDateString() === now.toDateString());
    if (filter === 'This Week') { const w = new Date(); w.setDate(now.getDate() - 7); return result.filter(s => new Date(s.date) >= w); }
    if (filter === 'This Month') return result.filter(s => { const d = new Date(s.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
    return result;
  };

  const filtered = getFiltered();
  const totalRevenue = filtered.reduce((sum, s) => sum + s.totalAmount, 0);
  const totalProfit = filtered.reduce((sum, s) => sum + s.profit, 0);
  const totalTransactions = filtered.length;
  const avgSale = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  const getLast7Days = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); days.push(d); }
    return days;
  };

  const chartData = getLast7Days().map(d => ({
    name: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    sales: sales.filter(s => !s.isVoided && new Date(s.date).toDateString() === d.toDateString()).reduce((sum, s) => sum + s.totalAmount, 0),
  }));

  const productMap = {};
  filtered.forEach(s => {
    if (!productMap[s.productName]) productMap[s.productName] = { revenue: 0, units: 0, profit: 0 };
    productMap[s.productName].revenue += s.totalAmount;
    productMap[s.productName].units += s.quantitySold;
    productMap[s.productName].profit += s.profit;
  });
  const topProducts = Object.entries(productMap).sort((a, b) => b[1].revenue - a[1].revenue).slice(0, 5);
  const bestDay = chartData.reduce((best, d) => d.sales > best.sales ? d : best, chartData[0]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.accent }}>Loading...</div>
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: COLORS.ink }}>Reports</div>
        <div style={{ fontSize: 13, color: COLORS.inkLight, marginTop: 4 }}>Business performance overview</div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {['Today', 'This Week', 'This Month', 'All Time'].map(tab => (
          <button key={tab} onClick={() => setFilter(tab)}
            style={{ padding: '8px 16px', borderRadius: 20, border: `1.5px solid ${filter === tab ? COLORS.accent : COLORS.border}`, background: filter === tab ? COLORS.accent : 'white', color: filter === tab ? 'white' : COLORS.inkLight, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : (isOwner ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'), gap: 12, marginBottom: 20 }}>
        {[
          { icon: '💰', label: 'Revenue', value: `₦${totalRevenue.toLocaleString()}`, color: COLORS.accent, bg: COLORS.accentLight },
          ...(isOwner ? [{ icon: '📈', label: 'Profit', value: `₦${totalProfit.toLocaleString()}`, color: '#C89B2A', bg: '#FDF6E3' }] : []),
          { icon: '🛒', label: 'Transactions', value: totalTransactions, color: COLORS.ink, bg: '#EEF2FF' },
          { icon: '📊', label: 'Avg Sale', value: `₦${Math.round(avgSale).toLocaleString()}`, color: COLORS.ink, bg: COLORS.accentLight },
        ].map(card => (
          <div key={card.label} style={{ background: 'white', borderRadius: 14, padding: 16, border: `1px solid ${COLORS.border}` }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, marginBottom: 10 }}>{card.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 3 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'white', borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, marginBottom: 16 }}>Sales Last 7 Days</div>
        {chartData.some(d => d.sales > 0) ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barSize={isMobile ? 20 : 28}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: COLORS.inkLight }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: COLORS.inkLight }} axisLine={false} tickLine={false} tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`₦${value.toLocaleString()}`, 'Sales']} contentStyle={{ borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <Bar dataKey="sales" fill={COLORS.accent} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.inkLight, fontSize: 13 }}>No sales data yet</div>
        )}
      </div>

      {/* Best day */}
      {bestDay && bestDay.sales > 0 && (
        <div style={{ background: COLORS.accentLight, borderRadius: 14, padding: 16, border: `1px solid #B6DFC7`, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 32 }}>🏆</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>Best Day This Week</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: COLORS.accent }}>₦{bestDay.sales.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: COLORS.inkMid }}>{bestDay.name}</div>
          </div>
        </div>
      )}

      {/* Top products */}
      {topProducts.length > 0 && (
        <div style={{ background: 'white', borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, marginBottom: 16 }}>Top Products</div>
          {topProducts.map(([name, data], index) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: index < topProducts.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: index === 0 ? '#FDF6E3' : COLORS.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: index === 0 ? '#C89B2A' : COLORS.accent, flexShrink: 0 }}>{index + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div style={{ fontSize: 11, color: COLORS.inkLight, marginTop: 2 }}>{data.units} units</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.ink }}>₦{data.revenue.toLocaleString()}</div>
                {isOwner && <div style={{ fontSize: 11, color: COLORS.accent, marginTop: 2 }}>+₦{data.profit.toLocaleString()}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}